import { TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { LoanFieldProps } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { getAvailablePagePermissions } from 'hooks/hasPagePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { getLoanFieldSubMenus } from 'utils/loanFieldSubMenus.util'
import { RenderInput } from 'utils/RenderInput'

import { SubmenuItem, subMenus } from './constant'

interface UwQuickLinksData {
  submenus: SubmenuItem[]
}

const defaultData: UwQuickLinksData = {
  submenus: [
    { key: 'globalNotes', isExternal: false },
    { key: 'loanStructure', isExternal: true },
    { key: 'loanApplication', isExternal: true },
    { key: 'appraisalReview', isExternal: false },
    { key: 'orderValuation', isExternal: false },
    { key: 'appraisalInformation', isExternal: false },
    { key: 'expirationDates', isExternal: false },
    { key: 'underwritingSummary', isExternal: false },
    { key: 'tasks', isExternal: true },
    { key: 'dataVerifyDrive', isExternal: false },
    { key: 'parties', isExternal: false },
    { key: 'budget', isExternal: false },
  ],
}

export const UnderwritingQuickLinksConfig = () => {
  const [pagePermissionConfig, loanGlobalStatus, loanFields] = useSelector((state: any) => [
    state.globalConfigReducer[GlobalConfigType.PagePermissions],
    state.loanGlobalStatus,
    state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps,
  ])

  const [isLoading, setIsLoading] = useState(false)
  const [localData, setLocalData] = useState<UwQuickLinksData>(defaultData)

  const availableSubmenusByPlan = useMemo(() => getAvailablePagePermissions(), [pagePermissionConfig])
  const loanFieldSubMenus = useMemo(() => getLoanFieldSubMenus(loanFields), [loanFields])

  const allSubMenus: Record<string, { title: string }> = useMemo(
    () => ({ ...subMenus, ...loanFieldSubMenus }),
    [loanFieldSubMenus],
  )

  const isSubmenuAllowed = (key: string) => !!loanFieldSubMenus[key] || availableSubmenusByPlan.includes(key)

  const normalizedSubmenus = useMemo(
    () => localData.submenus.filter((item) => isSubmenuAllowed(item.key)),
    [localData.submenus, availableSubmenusByPlan, loanFieldSubMenus],
  )

  useEffect(() => {
    setIsLoading(true)
    getAdminConfig('underwritingQuickLinks')
      .then((res) => {
        if (res && Array.isArray(res.submenus)) setLocalData(res)
        else setLocalData(defaultData)
      })
      .finally(() => setIsLoading(false))
  }, [])

  const update = (submenus: SubmenuItem[]) => setLocalData((prev) => ({ ...prev, submenus }))

  const handleAdd = (key: string) => {
    const current = cloneDeep(localData.submenus)
    if (!current.find((item) => item.key === key)) {
      update([...current, { key, isExternal: false }])
    }
  }

  const handleRemove = (key: string) => update(localData.submenus.filter((item) => item.key !== key))

  const handleExternalToggle = (key: string, isExternal: boolean) => {
    update(localData.submenus.map((item) => (item.key === key ? { ...item, isExternal } : item)))
  }

  const handleTitleChange = (key: string, title: string) => {
    update(
      localData.submenus.map((item) => {
        if (item.key !== key) return item
        const defaultTitle = allSubMenus[key]?.title
        if (!title.trim() || title === defaultTitle) {
          const next = { ...item }
          delete next.title
          return next
        }
        return { ...item, title }
      }),
    )
  }

  const handleSubmenuLoanStatusChange = (key: string, selected: Record<string, boolean>) => {
    update(localData.submenus.map((item) => (item.key === key ? { ...item, loanStatus: selected } : item)))
  }

  const handleSubmenuAccountTypesChange = (key: string, selected: Record<string, boolean>) => {
    update(localData.submenus.map((item) => (item.key === key ? { ...item, accountTypes: selected } : item)))
  }

  const handleMoveUp = (key: string) => {
    const arr = cloneDeep(localData.submenus)
    const i = arr.findIndex((item) => item.key === key)
    if (i > 0) {
      ;[arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]
      update(arr)
    }
  }

  const handleMoveDown = (key: string) => {
    const arr = cloneDeep(localData.submenus)
    const i = arr.findIndex((item) => item.key === key)
    if (i !== -1 && i < arr.length - 1) {
      ;[arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]
      update(arr)
    }
  }

  const handleSave = async () => {
    setIsLoading(true)
    await setAdminConfig('underwritingQuickLinks', localData)
    setIsLoading(false)
    toast('Underwriting Floater Quick Links saved', { type: 'success' })
  }

  const availableSubmenus = useMemo(
    () =>
      Object.entries(allSubMenus).filter(
        ([key]) => !normalizedSubmenus.find((item) => item.key === key) && isSubmenuAllowed(key),
      ),
    [normalizedSubmenus, allSubMenus, availableSubmenusByPlan, loanFieldSubMenus],
  )

  return (
    <div className="relative mt-4">
      <LayoutLoading show={isLoading} />

      <div className="flex items-center justify-end mb-4">
        <button onClick={handleSave} className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 shrink-0">
          Save
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selected */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Selected Submenus (In Order)</h3>
          <div className="space-y-2">
            {normalizedSubmenus.length === 0 ? (
              <div className="text-gray-500 italic p-3 bg-gray-50 rounded">No submenus selected</div>
            ) : (
              normalizedSubmenus.map((submenuItem, index) => {
                const submenuValue = allSubMenus[submenuItem.key]
                if (!submenuValue) return null
                const isFirst = index === 0
                const isLast = index === normalizedSubmenus.length - 1
                return (
                  <div
                    key={submenuItem.key}
                    className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded"
                  >
                    <div className="flex flex-col gap-1 pt-1">
                      <button
                        onClick={() => handleMoveUp(submenuItem.key)}
                        disabled={isFirst}
                        className={`px-2 py-1 text-xs rounded ${
                          isFirst
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-primary text-white hover:bg-blue-600'
                        }`}
                        title="Move Up"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => handleMoveDown(submenuItem.key)}
                        disabled={isLast}
                        className={`px-2 py-1 text-xs rounded ${
                          isLast
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-primary text-white hover:bg-blue-600'
                        }`}
                        title="Move Down"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="w-[80%] flex-1">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="font-medium">
                          {index + 1}. {submenuItem.title || submenuValue.title}
                        </div>
                        <span
                          className="text-red-500 p-1 hover-shadow1 cursor-pointer"
                          onClick={() => handleRemove(submenuItem.key)}
                          title="Remove"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </span>
                      </div>
                      <div className="pl-4 space-y-2">
                        <RenderInput
                          input={{
                            inputType: 'Text',
                            title: 'Custom Title (optional)',
                            value: submenuItem.title || '',
                            placeholder: submenuValue.title,
                          }}
                          Key={`title-${submenuItem.key}`}
                          onChange={(_: string, value: string) => handleTitleChange(submenuItem.key, value)}
                        />
                        <RenderInput
                          input={{
                            inputType: 'checkbox',
                            title: 'External Link (opens in new tab)',
                            value: submenuItem.isExternal || false,
                          }}
                          Key={`external-${submenuItem.key}`}
                          onChange={(_: string, value: boolean) => handleExternalToggle(submenuItem.key, value)}
                        />
                        <RenderInput
                          input={{
                            inputType: 'multiselect',
                            title: 'Loan Status',
                            options: loanGlobalStatus,
                            value: submenuItem.loanStatus || {},
                            defaultSelected: true,
                          }}
                          Key={`${submenuItem.key}-loanStatus`}
                          onChange={(_: string, value: Record<string, boolean>) =>
                            handleSubmenuLoanStatusChange(submenuItem.key, value)
                          }
                        />
                        <RenderInput
                          input={{
                            inputType: 'multiselect',
                            title: 'Allowed account types',
                            options: accountTypes,
                            value: submenuItem.accountTypes || {},
                            sort: true,
                            sortKey: 'value',
                            defaultSelected: true,
                          }}
                          Key={`${submenuItem.key}-accountTypes`}
                          onChange={(_: string, value: Record<string, boolean>) =>
                            handleSubmenuAccountTypesChange(submenuItem.key, value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Available */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Available Submenus</h3>
          <div className="space-y-2">
            {availableSubmenus.length === 0 ? (
              <div className="text-gray-500 italic p-3 bg-gray-50 rounded">All submenus are selected</div>
            ) : (
              availableSubmenus.map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="font-medium">{value.title}</div>
                  <button
                    onClick={() => handleAdd(key)}
                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                  >
                    Add
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
